// Ported from vlookup-dramatic/app/find-a-mentor/mentors-browser.tsx by scripts/revamp/port-design.js
// and re-pointed at the real mentors API (revamp plan task 15).
//
// The design's browser filtered 12 sample mentors in the browser. Here every
// control writes a query parameter — the SAME parameters the old /mentors page
// used, so old links keep working — and the list is fetched through
// mentorsAction.loadData(request, userId, "Mentor", ""):
//
//   search box            keyword=
//   sort                  sort=first_name&direction=asc|desc
//   availability          is_available=all|yes|no
//   domain checkboxes     domains=<area id>,<area id>   (ids from GET getAreasData)
//   special filters       mf_mental_health=1 / mf_certified_coach=1 / lgbtq_discussion=1
//   pagination            page=
//
// Fields the API does not provide as filters (seniority level, "open to" level)
// are not shown as filters (plan: no backend work). The design's sample mentors
// are only the placeholder before the first response arrives.
//
// The card, the profile dialog and the filter widgets live in mentor-cards.js,
// shared with the partner programme pages (src/revamp/pages/partner-mentoring).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Search, ArrowUpRight, ChevronLeft, ChevronRight } from "../../lib/icons";
import { mentorsV3, findMentorHero } from "../../lib/content";
import { ButtonLink } from "../../components/button-link";
import { FindMentorHeroCarousel } from "./hero-carousel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { cn } from "../../lib/utils";
import * as mentorsAction from "../../../actions/mentorsAction";
import { agent } from "../../../utils/agent";
import { LOAD_AREAS_LIST, GET_STATS } from "../../../utils/apiPath";
import { showToasterWithOk, UCWords } from "../../../utils/helpers";
import {
  SORT_OPTIONS,
  AVAILABILITY_OPTIONS,
  SPECIAL_PARAMS,
  LOGIN_PROMPT,
  fromApi,
  splitList,
  MentorCard,
  MentorDialog,
  PagerButton,
  pageWindow,
  FilterHeading,
  FilterCheck,
  SpecialFilterChip,
} from "./mentor-cards";

// The design's sample mentors, in the card shape, shown only before the first response.
const SAMPLE = mentorsV3.map((m, i) => ({
  id: "sample-" + i,
  name: m.name, role: m.role, company: m.company, country: m.country, flagSrc: "", flag: m.flag,
  domain: m.domain, domains: [m.domain], subAreas: [], available: m.available, img: m.img, bio: m.bio,
  linkedin: "", language: m.languages.join(" · "), openTo: m.openTo, special: [], engagements: 0, raw: null, sample: true,
}));

/* ── Hero ticker: live numbers where GET setting has them ──────────────────── */
const fmt = (n) => Number(n).toLocaleString("en-US");
function tickerFrom(setting, areaCount) {
  return findMentorHero.ticker.map((t) => {
    if (t.l === "Mentors" && setting?.mentors != null) return { ...t, n: fmt(setting.mentors) };
    if (t.l === "Countries" && setting?.countries != null) return { ...t, n: fmt(setting.countries) };
    if (t.l === "Domains" && areaCount) return { ...t, n: String(areaCount) };
    return t; // Specialisations / Male Allies: design figures (no API field)
  });
}

export function MentorsBrowser() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const mentorsData = useSelector((s) => s.mentorsData) || [];
  const pagination = useSelector((s) => s.pagination) || {};
  const isFetching = useSelector((s) => s.isFetching);
  const isAuth = useSelector((s) => s.isAuth);
  const userInfo = useSelector((s) => s.userInfo) || {};
  const setting = useSelector((s) => s.setting);

  // ── URL state ──
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const keywordParam = params.get("keyword") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const direction = params.get("direction") || "asc";
  const availability = params.get("is_available") || "all";
  const selectedDomains = splitList(params.get("domains"));
  const specialOn = SPECIAL_PARAMS.filter((p) => params.get(p) === "1");

  const [query, setQuery] = useState(keywordParam);
  useEffect(() => setQuery(keywordParam), [keywordParam]);

  const push = (mutate) => {
    const next = new URLSearchParams(location.search);
    mutate(next);
    history.push({ pathname: "/mentors", search: next.toString() ? "?" + next.toString() : "" });
  };
  const setParam = (key, value, { resetPage = true } = {}) =>
    push((p) => {
      if (value === "" || value == null) p.delete(key);
      else p.set(key, value);
      if (resetPage) p.delete("page");
    });

  // ── Data: same call as the old page, on every URL change ──
  useEffect(() => {
    const request = {};
    if (params.get("sort")) { request.sort = params.get("sort"); request.direction = params.get("direction"); }
    if (params.get("page")) request.page = params.get("page");
    if (params.get("keyword")) request.keyword = params.get("keyword");
    if (params.get("domains")) request.domains = params.get("domains");
    if (params.get("is_available")) request.is_available = params.get("is_available");
    if (params.get("programmes")) request.programmes = params.get("programmes");
    SPECIAL_PARAMS.forEach((k) => { if (params.get(k)) request[k] = params.get(k); });
    dispatch(mentorsAction.loadData(request, isAuth ? userInfo.id : 0, "Mentor", ""));
    // QA row 16: no scroll-to-top here — searching/filtering keeps the user's
    // scroll position; only pagination scrolls (to the grid, see goToPage).
  }, [dispatch, params, isAuth, userInfo.id]);

  // ── Domain list (GET getAreasData), as the old filter did ──
  const [areas, setAreas] = useState([]);
  useEffect(() => {
    let alive = true;
    agent.get(LOAD_AREAS_LIST, {}).then((res) => { if (alive) setAreas(res.data.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // ── Dynamic stats ticker (GET get-stats?category=Mentors) ──
  const [dynamicStats, setDynamicStats] = useState(null);
  useEffect(() => {
    let alive = true;
    agent
      .get(`${GET_STATS}?category=Mentors`)
      .then((res) => {
        if (!alive) return;
        const raw = res?.data?.data ?? res?.data;
        const rows = Array.isArray(raw) ? raw : [];
        const mentorRows = rows
          .filter((r) => {
            const isActive = r && r.status !== false && r.status !== 0 && r.status !== "false" && r.status !== "0";
            const isMentors = !r?.category || String(r.category).trim().toLowerCase() === "mentors";
            return isActive && isMentors && r.heading && r.count != null;
          })
          .sort((a, b) => {
            if (a.display_order != null && b.display_order != null) {
              return Number(a.display_order) - Number(b.display_order);
            }
            return 0;
          })
          .map((r) => ({
            id: r.id,
            n: r.count,
            l: r.heading,
          }));

        if (mentorRows.length > 0) {
          setDynamicStats(mentorRows);
        }
      })
      .catch(() => { });

    return () => {
      alive = false;
    };
  }, []);

  const loaded = pagination.current_page != null;
  const mentors = loaded ? mentorsData.map(fromApi) : SAMPLE;
  const totalPages = Number(pagination.total_pages) || 1;
  const current = Math.min(page, totalPages);
  const ticker = (dynamicStats && dynamicStats.length > 0)
    ? dynamicStats
    : tickerFrom(setting, areas.length);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const openMentor = (m) => { setSelected(m); setOpen(true); };

  const toggleDomain = (id) => {
    const next = selectedDomains.includes(String(id)) ? selectedDomains.filter((x) => x !== String(id)) : [...selectedDomains, String(id)];
    setParam("domains", next.join(","));
  };
  const toggleSpecial = (key) => setParam(key, specialOn.includes(key) ? "" : "1");
  const resetFilters = () => { setQuery(""); history.push("/mentors"); };
  const submitSearch = (e) => { e.preventDefault(); setParam("keyword", query.trim()); };

  // QA row 16: pagination scrolls to the top of the results grid (not the page).
  const resultsRef = useRef(null);
  const goToPage = (p) => {
    setParam("page", String(p), { resetPage: false });
    if (resultsRef.current) resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Sticky search bar detection when scrolling past hero
  const [isSticky, setIsSticky] = useState(false);
  const searchBarRef = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      if (searchBarRef.current) {
        const rect = searchBarRef.current.getBoundingClientRect();
        setIsSticky(rect.top <= 78);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ── "Indicate as Preferred Mentor": exactly the old page's behaviour ──
  const ctaFor = (m) => {
    if (!m || m.sample) return { label: "Indicate as Preferred Mentor", onClick: () => {} };
    if (!isAuth) {
      // QA row 24: the old page showed guests the same select/waitlist label the
      // mentor's availability dictates; either way the click sends them to log in
      // (the old page's select CTA did exactly this — waitlisting needs an account too).
      const r = m.raw;
      const promptLogin = () => { showToasterWithOk(LOGIN_PROMPT); history.push("/login"); };
      const available = r.is_reached_limit === 0 && r.user_availability === 1;
      return { label: available ? "Indicate as Preferred Mentor" : "Waitlist for availability", onClick: promptLogin };
    }
    if (userInfo.role_id === "Mentor") return null; // mentors don't pick mentors
    const r = m.raw;
    if (r.is_relationship !== 0 || r.is_mentor_terminate !== 0 || r.is_same_program !== 1) return null;
    const request = { userId: userInfo.id, mentorId: m.id };
    if (r.is_reached_limit === 0 && r.user_availability === 1) {
      return { label: "Indicate as Preferred Mentor", onClick: () => dispatch(mentorsAction.saveSelectMentor(request)) };
    }
    return { label: "Waitlist for availability", onClick: () => dispatch(mentorsAction.saveWaitlistMentor(request)) };
  };

  return (
    <div className="overflow-x-clip">
      {/* ───────────────  HERO  ─────────────── */}
      <section className="relative isolate overflow-hidden">
        <div className="pointer-events-none absolute -right-40 -top-52 -z-10 size-[600px] rounded-full bg-brand-blue/15 blur-3xl" />
        <div className="pointer-events-none absolute -left-44 top-32 -z-10 size-[420px] rounded-full bg-brand-blue/15 blur-3xl" />

        <div className="wrap grid items-start gap-x-10 gap-y-8 py-10 lg:grid-cols-[1.05fr_0.95fr] lg:py-16">
          <div>
            <span className="inline-flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-red">
              <span className="h-px w-8 bg-current opacity-50" />
              Our Mentor Community
            </span>
            <h1 className="mt-5 text-5xl font-semibold leading-[0.95] tracking-tight text-heading sm:text-6xl lg:text-7xl">
              Find a mentor for your{" "}
              <em className="font-normal italic text-brand-red">career goals</em>.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
              {findMentorHero.sub}
            </p>

            {!isAuth && (
            <div className="mt-7">
              <ButtonLink
                href={findMentorHero.cta.href}
                className="group h-12 rounded-full bg-brand-red px-7 text-sm font-bold text-white shadow-lg shadow-brand-red/25 hover:bg-brand-red-dark"
              >
                {findMentorHero.cta.label}
                <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </ButtonLink>
            </div>
            )}

            <dl className={cn(
              "mt-10 grid gap-x-6 gap-y-6 border-t border-brand-navy/10 pt-7",
              ticker.length === 3 && "grid-cols-3",
              ticker.length === 4 && "grid-cols-2 sm:grid-cols-4",
              ticker.length >= 5 && "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5",
              (![3, 4].includes(ticker.length) && ticker.length < 5) && "grid-cols-2 sm:grid-cols-4"
            )}>
              {ticker.map((s, idx) => (
                <div key={s.id != null ? `${s.id}-${idx}` : `${s.l}-${idx}`} className="min-w-0">
                  <dt className="font-display text-2xl font-semibold text-brand-navy md:text-3xl xl:text-4xl">
                    {s.n}
                  </dt>
                  <dd className="mt-1 text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    {s.l}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <FindMentorHeroCarousel />
        </div>
      </section>

      {/* ───────────────  SEARCH (Sticky on scroll)  ─────────────── */}
      <section
        ref={searchBarRef}
        className={cn(
          "mentor-search-bar w-full",
          isSticky && "mentor-search-bar--stuck"
        )}
      >
        <div className="wrap mentor-search">
          <form onSubmit={submitSearch} className="mentor-search__form flex items-center gap-1.5 rounded-full bg-card py-1.5 pl-1.5 pr-4 shadow-lg ring-1 ring-brand-navy/10 md:pr-1.5 md:gap-2">
            <Search className="mentor-search__icon ml-3 size-5 shrink-0 text-muted-foreground sm:ml-4" />
            <input
              type="search"
              enterKeyHint="search"
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, country, organisation or role"
              aria-label="Search mentors"
              className="h-10 min-w-0 flex-1 bg-transparent px-2 text-sm text-brand-navy outline-none placeholder:text-muted-foreground"
            />
            <button type="submit" className="group hidden shrink-0 items-center gap-1.5 rounded-full bg-brand-red px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-red-dark md:inline-flex">
              Search
              <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </div>
      </section>

      {/* ───────────────  FILTERS + RESULTS  ─────────────── */}
      <section ref={resultsRef} className="wrap scroll-mt-[170px] pb-16 pt-6 lg:pb-20">
        <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-[220px_minmax(0,1fr)] md:gap-5 lg:grid-cols-[264px_minmax(0,1fr)] lg:gap-6">
          {/* ── Sidebar (side-by-side from tablet up — QA row 48) ── */}
          <aside className="mentors-filters rounded-3xl bg-card shadow-sm ring-1 ring-brand-navy/5">
            <div className="flex items-center justify-between border-b border-brand-navy/10 px-5 py-4">
              <span className="text-[15px] font-extrabold text-brand-navy">Filters</span>
              <button type="button" onClick={resetFilters} className="text-xs font-bold text-brand-red hover:underline">
                Reset Filters
              </button>
            </div>

            <div className="mentors-filters__domains px-5 py-4">
              <FilterHeading>Select Domain</FilterHeading>
              {areas.map((a) => (
                <FilterCheck
                  key={a.id}
                  label={UCWords(a.name)}
                  checked={selectedDomains.includes(String(a.id))}
                  onChange={() => toggleDomain(a.id)}
                />
              ))}
              {areas.length === 0 && <p className="py-1 text-xs text-muted-foreground">Loading domains…</p>}
            </div>

            {/* Pinned at the bottom of the sticky sidebar so it stays in view
                while the domain list / mentor grid scroll. */}
            <div className="mentors-filters__support border-t border-brand-navy/10 px-5 py-4">
              <FilterHeading>Areas of Support</FilterHeading>
              <div className="relative z-20 flex items-center gap-2">
                {SPECIAL_PARAMS.map((p, i) => (
                  <SpecialFilterChip
                    key={p}
                    param={p}
                    checked={specialOn.includes(p)}
                    onChange={() => toggleSpecial(p)}
                    align={i === 0 ? "left" : i === SPECIAL_PARAMS.length - 1 ? "right" : "center"}
                  />
                ))}
              </div>
            </div>
          </aside>

          {/* ── Results (min height so refetches don't collapse the page — QA row 16) ── */}
          <div className="min-h-[60vh]">
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground" aria-live="polite">
                {loaded ? (
                  <>
                    Showing <strong className="font-bold text-brand-navy">{pagination.count || 0}</strong> of{" "}
                    <strong className="font-bold text-brand-navy">{pagination.total || 0}</strong> mentors
                    {totalPages > 1 && <> · page {current} of {totalPages}</>}
                  </>
                ) : (
                  <>Loading mentors…</>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Select
                  value={direction}
                  onValueChange={(v) => push((p) => { p.set("sort", "first_name"); p.set("direction", v); p.delete("page"); })}
                >
                  <SelectTrigger className="h-11 rounded-full border-brand-navy/15 bg-cream px-5 text-sm font-semibold text-brand-navy">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((o) => (
                      <SelectItem key={o.direction} value={o.direction}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={availability} onValueChange={(v) => setParam("is_available", v)}>
                  <SelectTrigger className="h-11 rounded-full border-brand-navy/15 bg-cream px-5 text-sm font-semibold text-brand-navy">
                    <SelectValue placeholder="Availability" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABILITY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loaded && mentors.length === 0 ? (
              <div className="rounded-3xl bg-card p-16 text-center shadow-sm ring-1 ring-brand-navy/5">
                <p className="font-display text-2xl font-semibold text-brand-navy">No mentors match your search.</p>
                <button type="button" onClick={resetFilters} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-red">
                  Clear all filters
                  <ArrowUpRight className="size-4" />
                </button>
              </div>
            ) : (
              <div className={cn("grid gap-6 sm:grid-cols-2 lg:grid-cols-3 transition-opacity", (!loaded || isFetching) && "opacity-60")}>
                {mentors.map((m) => (
                  <MentorCard key={m.id} mentor={m} cta={ctaFor(m)} onOpen={openMentor} />
                ))}
              </div>
            )}

            {/* pagination (server-side: pagination.total_pages) */}
            {loaded && mentors.length > 0 && totalPages > 1 && (
              <div className="mt-12 flex flex-wrap items-center justify-center gap-2">
                <PagerButton disabled={current === 1} onClick={() => goToPage(current - 1)} aria-label="Previous page">
                  <ChevronLeft className="size-4" />
                </PagerButton>
                {pageWindow(current, totalPages).map((p, i) =>
                  p === "…" ? (
                    <span key={"gap" + i} className="px-1 text-sm font-bold text-brand-navy/40">…</span>
                  ) : (
                    <PagerButton key={p} active={current === p} onClick={() => goToPage(p)}>
                      {p}
                    </PagerButton>
                  )
                )}
                <PagerButton disabled={current === totalPages} onClick={() => goToPage(current + 1)} aria-label="Next page">
                  <ChevronRight className="size-4" />
                </PagerButton>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ───────────────  MODAL  ─────────────── */}
      <MentorDialog open={open} onOpenChange={setOpen} mentor={selected} cta={ctaFor(selected)} />
    </div>
  );
}
